import React, { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import { Modal, Button } from '@patternfly/react-core';

type SessionTimeoutProps = {
	showModal: boolean;
	setShowModal: Dispatch<SetStateAction<boolean>>;
	extendSession: () => void;
	logout: () => void;
};

export const SessionTimeout: React.FC<SessionTimeoutProps> = ({
	showModal,
	setShowModal,
	extendSession,
	logout,
}) => {
	// 5-minute countdown in seconds
	const COUNTDOWN_SECONDS = 5 * 60;
	const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS);
	const countdownRef = useRef<NodeJS.Timeout | null>(null);

	// Start countdown when modal opens
	useEffect(() => {
		if (!showModal) return;

		// Reset to full countdown
		setRemaining(COUNTDOWN_SECONDS);

		// Clear previous interval if any
		if (countdownRef.current) clearInterval(countdownRef.current);

		countdownRef.current = setInterval(() => {
			setRemaining((prev) => {
				if (prev <= 1) {
					clearInterval(countdownRef.current!);
					countdownRef.current = null;
					localStorage.setItem('hasSessionInactivityTimeout', '1');
					logout();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		// Clean up on modal close
		return () => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
				countdownRef.current = null;
			}
		};
	}, [showModal, logout]);

	const handleExtend = () => {
		// Stop countdown immediately
		if (countdownRef.current) {
			clearInterval(countdownRef.current);
			countdownRef.current = null;
		}
		// Notify parent to extend session
		extendSession();
		setShowModal(false);
	};

	const formatTime = (seconds: number) => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
	};

	return (
		<Modal
			title="Session Expiring"
			isOpen={showModal}
			showClose={false}
			variant="small"
			actions={[
				<Button
					key="continue"
					variant="primary"
					onClick={handleExtend}
				>
					Continue Session
				</Button>,
				<Button
					key="logout"
					variant="secondary"
					onClick={logout}
				>
					Logout
				</Button>,
			]}
		>
			<p>Your session will expire in {formatTime(remaining)}.</p>
			<p>Do you want to continue your session?</p>
		</Modal>
	);
};
